import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { dashboardService } from '../../services/api';

const SalesStatisticOne = () => {
    const [revenueData, setRevenueData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('Weekly');

    useEffect(() => {
        loadRevenueTrend();
    }, [period]);

    const loadRevenueTrend = async () => {
        try {
            setLoading(true);
            const response = await dashboardService.getRevenueTrend(null, null, period);
            if (response.success && response.data) {
                setRevenueData(response.data);
            }
        } catch (error) {
            console.error('Error loading revenue trend:', error);
        } finally {
            setLoading(false);
        }
    };

    const chartOptions = {
        chart: {
            type: 'area',
            toolbar: {
                show: false
            },
            zoom: {
                enabled: false
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        xaxis: {
            categories: revenueData.map(item => {
                if (period === 'Monthly') {
                    // Format: "2024-01" -> "Ocak 2024"
                    const [year, month] = item.date.split('-');
                    const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
                    return `${monthNames[parseInt(month) - 1]} ${year}`;
                } else if (period === 'Weekly') {
                    const date = new Date(item.date);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                } else if (period === 'Yearly') {
                    return item.date;
                } else {
                    const date = new Date(item.date);
                    return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:00`;
                }
            }),
            labels: {
                style: {
                    colors: '#6b7280',
                    fontSize: '12px'
                },
                rotate: period === 'Monthly' ? -45 : 0
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#6b7280',
                    fontSize: '12px'
                },
                formatter: function (val) {
                    return '₺' + val.toLocaleString('tr-TR');
                }
            }
        },
        tooltip: {
            y: {
                formatter: function (val) {
                    return '₺' + val.toLocaleString('tr-TR');
                }
            }
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.3,
                stops: [0, 90, 100]
            }
        },
        colors: ['#3b82f6']
    };

    const chartSeries = [{
        name: 'Gelir',
        data: revenueData.map(item => item.daily_revenue || 0)
    }];

    // Calculate totals safely
    const totalRevenue = Array.isArray(revenueData) && revenueData.length > 0 
        ? revenueData.reduce((sum, item) => {
            const revenue = parseFloat(item.daily_revenue) || 0;
            return sum + revenue;
        }, 0)
        : 0;
    
    const avgDaily = revenueData.length > 0 && totalRevenue > 0 
        ? totalRevenue / revenueData.length 
        : 0;
    
    const lastRevenue = revenueData.length > 0 
        ? parseFloat(revenueData[revenueData.length - 1]?.daily_revenue) || 0 
        : 0;
    
    const prevRevenue = revenueData.length > 1 
        ? parseFloat(revenueData[revenueData.length - 2]?.daily_revenue) || 0 
        : 0;
    
    const percentChange = prevRevenue > 0 
        ? parseFloat(((lastRevenue - prevRevenue) / prevRevenue * 100).toFixed(1))
        : (lastRevenue > 0 ? 100 : 0);

    return (
        <div className="col-xxl-6 col-xl-12">
            <div className="card h-100">
                <div className="card-body">
                    <div className="d-flex flex-wrap align-items-center justify-content-between">
                        <h6 className="text-lg mb-0">Gelir Trendi</h6>
                        <select 
                            className="form-select bg-base form-select-sm w-auto" 
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                        >
                            <option value="Weekly">Haftalık</option>
                            <option value="Monthly">Aylık</option>
                            <option value="Yearly">Yıllık</option>
                            <option value="Today">Günlük</option>
                        </select>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-8">
                        <h6 className="mb-0">₺{totalRevenue.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</h6>
                        {percentChange !== 0 && !isNaN(percentChange) && (
                            <span className={`text-sm fw-semibold rounded-pill ${percentChange >= 0 ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} border ${percentChange >= 0 ? 'br-success' : 'br-danger'} px-8 py-4 line-height-1 d-flex align-items-center gap-1`}>
                                {Math.abs(percentChange)}% <Icon icon={percentChange >= 0 ? "bxs:up-arrow" : "bxs:down-arrow"} className="text-xs" />
                            </span>
                        )}
                        {!isNaN(avgDaily) && avgDaily > 0 && (
                            <span className="text-xs fw-medium">
                              {period === 'Monthly' ? `Ortalama: ₺${avgDaily.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/Ay` :
                               period === 'Weekly' ? `Ortalama: ₺${avgDaily.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/Hafta` :
                               period === 'Yearly' ? `Ortalama: ₺${avgDaily.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/Yıl` :
                               `Ortalama: ₺${avgDaily.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/Gün`}
                            </span>
                        )}
                    </div>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '264px' }}>
                            <p className="text-secondary-light">Yükleniyor...</p>
                        </div>
                    ) : revenueData.length > 0 ? (
                        <ReactApexChart options={chartOptions} series={chartSeries} type="area" height={264} />
                    ) : (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '264px' }}>
                            <p className="text-secondary-light">Veri bulunamadı</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalesStatisticOne;
